import type { AssetId } from "@visioni/a3s-schema";
import type { ContractWindow } from "@visioni/a3s-monitoring-contract";

/**
 * One appended row of the Hub Asset Event Log, in `change_seq` order.
 * Shared by independent consumers: adding a field is safe, changing one is not.
 */
export type HubEventChange = {
  readonly asset: AssetId;
  readonly windows: readonly ContractWindow[];
  /**
   * The log's own `change_seq`, as text because it is a BIGINT. The only
   * ordering key - monotonic, so it holds live and on replay alike.
   */
  readonly changeSeq: string;
  /**
   * The hub's timestamp - when a feature was granted or revoked, never an
   * ordering key, because the hub's clock can go backwards.
   */
  readonly eventTs: Date;
  /**
   * Raw `event_type`. Compare against `EventType.enum` rather than narrowing:
   * a replay must not throw on a type the schema has since dropped.
   */
  readonly eventType: string;
  /**
   * Raw hire purposes; translating them to Features is a3s-features' job.
   * `null` means the row never recorded them - distinct from `[]`.
   */
  readonly hirePurposes: readonly string[] | null;
  /** Whether the hub still considers the asset a3s-managed on this event. */
  readonly isA3sManaged: boolean;
  /**
   * The stored hub event, exactly as appended. Unparsed: decoding it is the
   * consumer's job, so a row the schema has since outgrown stalls only that one.
   */
  readonly detail: unknown;
};
